import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { glassCardClass } from "@/recruitment/styles.js";

const MAIN_APP_LOGIN_URL = import.meta.env.VITE_MAIN_APP_LOGIN_URL || "https://kconnect.pl/login";

const initialForm = {
  branch_id: "",
  campaign_name: "",
  school_year: "",
  public_slug: "",
  intake_start_at: "",
  intake_end_at: "",
  decision_publish_at: "",
  capacity: "25",
  scoring_model_name: "",
  scoring_model_description: "",
  scoring_rules_json: '{\n  "manualPointsEnabled": true\n}',
  tie_breaker_config_json: '["submissionTimestamp"]',
};

function getSchoolYearOptions() {
  const currentYear = new Date().getFullYear();
  const options = [];

  for (let startYear = currentYear - 1; startYear <= currentYear + 1; startYear += 1) {
    options.push(`${startYear}-${startYear + 1}`);
  }

  return options;
}

export default function AdminCampaignCreatePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [branch, setBranch] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);

  const schoolYearOptions = useMemo(() => getSchoolYearOptions(), []);

  const canManageRecruitment = useMemo(() => {
    if (!profile) return false;
    const roles = profile.roles || [];
    return roles.includes("branch_admin") || roles.includes("super_admin");
  }, [profile]);

  const getSessionFromHash = () => {
    const rawHash = window.location.hash || "";
    if (!rawHash.startsWith("#")) return null;

    const params = new URLSearchParams(rawHash.slice(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");

    if (!accessToken || !refreshToken) return null;
    return { access_token: accessToken, refresh_token: refreshToken };
  };

  const clearHash = () => {
    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, cleanUrl);
  };

  const redirectToMainLogin = () => {
    const targetUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    try {
      const loginUrl = new URL(MAIN_APP_LOGIN_URL, window.location.origin);
      loginUrl.searchParams.set("next", targetUrl);
      window.location.href = loginUrl.toString();
      return;
    } catch (_) {
      const fallback = new URL("https://kconnect.pl/login");
      fallback.searchParams.set("next", targetUrl);
      window.location.href = fallback.toString();
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        let user = null;

        const {
          data: { user: existingUser },
          error: authError,
        } = await supabase.auth.getUser();
        if (authError && !String(authError.message || "").toLowerCase().includes("auth session missing")) {
          throw authError;
        }
        user = existingUser;

        if (!user) {
          const hashSession = getSessionFromHash();
          if (hashSession) {
            const { error: setSessionError } = await supabase.auth.setSession(hashSession);
            if (setSessionError) throw setSessionError;

            clearHash();
            const {
              data: { user: userAfterSessionSet },
              error: refreshUserError,
            } = await supabase.auth.getUser();
            if (refreshUserError) throw refreshUserError;
            user = userAfterSessionSet;
          }
        }

        if (!user) {
          redirectToMainLogin();
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from("app_users")
          .select("id, roles, branch_ids")
          .eq("id", user.id)
          .maybeSingle();
        if (profileError) throw profileError;
        setProfile(profileData ?? null);

        const currentBranchId = profileData?.branch_ids?.[0] ?? "";
        setForm((prev) => ({
          ...prev,
          branch_id: currentBranchId,
          school_year: prev.school_year || schoolYearOptions[0] || "",
        }));

        if (currentBranchId) {
          const { data: branchData, error: branchError } = await supabase
            .from("branches")
            .select("id, name")
            .eq("id", currentBranchId)
            .maybeSingle();
          if (branchError) throw branchError;
          setBranch(branchData ?? null);
        }
      } catch (err) {
        const message = String(err?.message || "");
        if (message.toLowerCase().includes("auth session missing")) {
          redirectToMainLogin();
          return;
        }
        toast.error(message || "Nie udało się załadować strony tworzenia kampanii.");
        navigate("/admin", { replace: true });
      } finally {
        setLoadingProfile(false);
      }
    };

    bootstrap();
  }, [navigate, schoolYearOptions]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const createCampaign = async (event) => {
    event.preventDefault();
    if (!canManageRecruitment) {
      toast.error("Brak uprawnień do tworzenia kampanii.");
      return;
    }
    if (!form.branch_id) {
      toast.error("Brak przypisanej placówki. Skontaktuj się z administratorem.");
      return;
    }

    let rules;
    let tieBreakerConfig;

    try {
      rules = JSON.parse(form.scoring_rules_json || "{}");
      tieBreakerConfig = JSON.parse(form.tie_breaker_config_json || "[]");
    } catch {
      toast.error("JSON punktacji lub tie-breakerów jest niepoprawny.");
      return;
    }

    setSaving(true);
    try {
      const { data: scoringModel, error: scoringError } = await supabase
        .from("recruitment_scoring_models")
        .insert({
          branch_id: form.branch_id,
          name: form.scoring_model_name,
          description: form.scoring_model_description || null,
          rules,
          tie_breaker_options: tieBreakerConfig,
        })
        .select("id")
        .single();
      if (scoringError) throw scoringError;

      const { error: campaignError } = await supabase.rpc("recruitment_create_campaign", {
        p_branch_id: form.branch_id,
        p_campaign_name: form.campaign_name,
        p_public_slug: form.public_slug,
        p_intake_start_at: form.intake_start_at,
        p_intake_end_at: form.intake_end_at,
        p_decision_publish_at: form.decision_publish_at,
        p_capacity: Number(form.capacity),
        p_scoring_model_id: scoringModel.id,
        p_tie_breaker_config: tieBreakerConfig,
        p_settings: {
          schoolYear: form.school_year,
        },
      });
      if (campaignError) throw campaignError;

      toast.success("Kampania została utworzona.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err.message || "Nie udało się utworzyć kampanii.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingProfile) {
    return <p className="text-sm text-slate-500">Ładowanie strony...</p>;
  }

  if (!canManageRecruitment) {
    return (
      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Brak dostępu</CardTitle>
          <CardDescription>Ta strona jest dostępna dla ról `branch_admin` i `super_admin`.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Button asChild variant="outline" size="sm">
        <Link to="/admin" className="inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Wróć
        </Link>
      </Button>

      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Nowa kampania</CardTitle>
          <CardDescription>Po starcie naboru ustawienia krytyczne kampanii zostają zablokowane.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={createCampaign} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="branch_name">Placówka</Label>
              <Input id="branch_name" value={branch?.name || "Brak przypisanej placówki"} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign_name">Nazwa kampanii</Label>
              <Input id="campaign_name" value={form.campaign_name} onChange={(e) => setField("campaign_name", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="school_year">Docelowy rok szkolny</Label>
              <Select value={form.school_year} onValueChange={(value) => setField("school_year", value)}>
                <SelectTrigger id="school_year">
                  <SelectValue placeholder="Wybierz rok szkolny" />
                </SelectTrigger>
                <SelectContent>
                  {schoolYearOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="public_slug">Public slug</Label>
              <Input
                id="public_slug"
                value={form.public_slug}
                onChange={(e) => setField("public_slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity">Liczba miejsc</Label>
              <Input id="capacity" type="number" min={1} value={form.capacity} onChange={(e) => setField("capacity", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="intake_start_at">Start procesu</Label>
              <Input
                id="intake_start_at"
                type="datetime-local"
                value={form.intake_start_at}
                onChange={(e) => setField("intake_start_at", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="intake_end_at">Koniec przyjmowania wniosków</Label>
              <Input
                id="intake_end_at"
                type="datetime-local"
                value={form.intake_end_at}
                onChange={(e) => setField("intake_end_at", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="decision_publish_at">Publikacja wyników</Label>
              <Input
                id="decision_publish_at"
                type="datetime-local"
                value={form.decision_publish_at}
                onChange={(e) => setField("decision_publish_at", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scoring_model_name">Nazwa modelu punktacji</Label>
              <Input
                id="scoring_model_name"
                value={form.scoring_model_name}
                onChange={(e) => setField("scoring_model_name", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="scoring_model_description">Opis modelu punktacji</Label>
              <Textarea
                id="scoring_model_description"
                value={form.scoring_model_description}
                onChange={(e) => setField("scoring_model_description", e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="scoring_rules_json">Reguły punktacji (JSON)</Label>
              <Textarea
                id="scoring_rules_json"
                className="min-h-[120px] font-mono text-xs"
                value={form.scoring_rules_json}
                onChange={(e) => setField("scoring_rules_json", e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="tie_breaker_config_json">Konfiguracja tie-breaker (JSON)</Label>
              <Textarea
                id="tie_breaker_config_json"
                className="min-h-[80px] font-mono text-xs"
                value={form.tie_breaker_config_json}
                onChange={(e) => setField("tie_breaker_config_json", e.target.value)}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit" disabled={saving || !form.branch_id} className="inline-flex items-center gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? "Zapisywanie..." : "Utwórz kampanię"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/admin")}>
                Anuluj
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
